/**
 * SOLID Review
 * S — Strong. One concern: the guest's nickname form — the name typed, its submission (the guest made, or why not, within ten
 *     seconds), the way back to the guest choice. Whether the modal is open is `IdentityModalController`'s (AppRoot's).
 * O — Good. A new refusal is one more arm of the message.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. Type, submit, cancel, a key — and the view's one input.
 * D — Strong. Two use cases, the request contexts, the timers, and a focus the view hands it.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { IServiceViewTimers } from './platform/IConfigDepViewPlatform';
import type { ClientRequests } from '../client/ClientRequests';
import { SERVER_REFUSED } from '../../application/ports/contracts';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

/** How long the modal waits for the server before it says so — and the deadline its call carries. */
const TIMEOUT_MS = 10_000;
const TIMED_OUT = 'Request timed out. Server may be unreachable.';
/** Back out of the guest nickname step to the "Play as a guest?" choice. */
const BACK_TO_GUEST_CHOICE: TClientUseCaseInput<'navigate'> = { screen: 'GUEST_CHOICE' };
/** The Domain's refusal of a name the player typed. */
const INVALID_NAME = 'INVALID_NAME';
/** The input takes the focus a moment after the modal opens. */
const FOCUS_DELAY_MS = 80;

export interface IStateIdentityForm {
  readonly displayName: string;
  readonly validationError: string | null;
  readonly isSubmitting: boolean;
}

/** What the modal's view hands it: whether it is open. */
type TFormInputs = readonly [isOpen: boolean];

const BLANK: IStateIdentityForm = { displayName: '', validationError: null, isSubmitting: false };
const OPENED: Partial<IStateIdentityForm> = { displayName: '', validationError: null };
const SUBMITTING: Partial<IStateIdentityForm> = { validationError: null, isSubmitting: true };
const SUBMITTED: Partial<IStateIdentityForm> = { isSubmitting: false };

/**
 * THE GUEST'S NICKNAME FORM (frontend-framework-oo P4, 2026-10-29, §28.296) — `components/game/IdentityModal.tsx`'s state, its effect
 * and its handlers. Each opening clears the form and focuses the input a moment later (the focus is the view's, handed in once). A
 * submission waits ten seconds at most — its own timer races the call, whose deadline is the same — and reads a refusal as the
 * Domain's or the server's words, a missed deadline as the timeout.
 */
export class IdentityModalViewModel implements IServiceViewModel<IStateIdentityForm> {
  private readonly state = new ObservableState<IStateIdentityForm>(BLANK);
  private readonly reactions = new Reactions<TFormInputs>([{
    deps: (inputs) => inputs,
    run: ([isOpen]) => {
      if (!isOpen) return;
      this.state.update(OPENED);
      this.timers.setTimeout(() => this.focus(), FOCUS_DELAY_MS);
    },
  }]);

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'createGuest' | 'navigate'>,
    private readonly requests: ClientRequests,
    private readonly timers: IServiceViewTimers,
    private readonly focus: () => void,
  ) {}

  readonly type = (displayName: string): void => {
    const typed: Partial<IStateIdentityForm> = { displayName, validationError: null };
    this.state.update(typed);
  };

  readonly submit = async (close: () => void): Promise<void> => {
    this.state.update(SUBMITTING);
    try {
      const timeoutPromise = new Promise<never>((_, reject) =>
        this.timers.setTimeout(() => reject(new Error(TIMED_OUT)), TIMEOUT_MS)
      );
      const guest: TClientUseCaseInput<'createGuest'> = { displayName: this.state.get().displayName };
      const result = await Promise.race([this.useCases.createGuest(this.requests.context(TIMEOUT_MS), guest), timeoutPromise]);
      if (!result.success) {
        const { code, message } = result.error;
        this.refuse(code === INVALID_NAME ? message
          : code === SERVER_REFUSED ? `Server error: ${message}`
            : code === 'DEADLINE' ? TIMED_OUT : 'Unexpected error. Try again.');
        return;
      }
      close();
    } catch (err) {
      /* c8 ignore next -- the non-Error arm is unreachable through the Framework's socket: the kernel's guard answers a rejected use
         case with INTERNAL (read above as the generic line), so the one rejection is this form's own timer's, an Error
         (frontend-framework-oo P0, §28.292). */
      this.refuse(err instanceof Error ? err.message : 'Unexpected error. Try again.');
    } finally {
      this.state.update(SUBMITTED);
    }
  };

  /** Back to the guest choice: the modal closes first. */
  readonly cancel = (close: () => void): void => {
    close();
    void this.useCases.navigate(this.requests.context(), BACK_TO_GUEST_CHOICE);
  };

  /** Enter submits, Escape cancels. */
  readonly keyDown = (key: string, close: () => void): void => {
    if (key === 'Enter') void this.submit(close);
    if (key === 'Escape') this.cancel(close);
  };

  private refuse(validationError: string): void {
    const refused: Partial<IStateIdentityForm> = { validationError };
    this.state.update(refused);
  }

  getSnapshot(): IStateIdentityForm {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as TFormInputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as TFormInputs);
  }
}
